import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import { useToast } from '../contexts/ToastContext';
import { 
  standardizeError, 
  createErrorContext, 
  logError, 
  getRecoveryStrategies,
  ERROR_RECOVERY_STRATEGIES 
} from '../utils/consolidatedErrorHandling.js';

/**
 * Enhanced API hook with standardized error handling, retry mechanisms, and loading states
 */
export const useEnhancedApi = (options = {}) => {
  const { getToken } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;

  const {
    component = 'Unknown',
    showErrorToasts = true,
    showSuccessToasts = false,
    autoRetry = true,
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * Execute API call with enhanced error handling
   */
  const execute = useCallback(async (apiCall, callOptions = {}) => {
    const {
      operation = 'API Call',
      showErrorToast = showErrorToasts,
      showSuccessToast = showSuccessToasts,
      successMessage = null,
      loadingState = true,
      onSuccess = null,
      onError = null,
      context = {}
    } = callOptions;

    const errorContext = createErrorContext(operation, component, context);

    try {
      if (loadingState) setLoading(true);
      setError(null);

      const result = await apiCall(apiClient);
      
      // Reset retry count on success
      setRetryCount(0);
      
      if (showSuccessToast && successMessage) {
        showSuccess(successMessage);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (err) {
      const standardizedError = standardizeError(err, errorContext);
      
      // Log error for monitoring
      logError(standardizedError, errorContext);
      
      setError(standardizedError);
      
      if (showErrorToast) {
        showError(standardizedError.userMessage);
      }
      
      if (onError) {
        onError(standardizedError);
      }
      
      throw standardizedError;
    } finally {
      if (loadingState) setLoading(false);
    }
  }, [apiClient, showError, showSuccess, component, showErrorToasts, showSuccessToasts]);

  /**
   * Execute API call with automatic retry logic
   */
  const executeWithRetry = useCallback(async (apiCall, callOptions = {}) => {
    const {
      maxRetries: callMaxRetries = maxRetries,
      retryDelay: callRetryDelay = retryDelay,
      ...otherOptions
    } = callOptions;

    let lastError;
    let attempt = 0;

    while (attempt <= callMaxRetries) {
      try {
        if (attempt > 0) {
          setIsRetrying(true);
          setRetryCount(attempt);
        }

        const result = await execute(apiCall, {
          ...otherOptions,
          showErrorToast: attempt === callMaxRetries // Only show error on final attempt
        });

        setIsRetrying(false);
        setRetryCount(0);
        return result;
      } catch (err) {
        lastError = err;
        
        // Don't retry if error is not retryable
        if (!err.isRetryable || attempt === callMaxRetries) {
          setIsRetrying(false);
          throw err;
        }

        // Calculate delay for next retry
        const delay = err.getRetryDelay ? err.getRetryDelay(attempt) : callRetryDelay * Math.pow(2, attempt);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
      }
    }

    setIsRetrying(false);
    throw lastError;
  }, [execute, maxRetries, retryDelay]);

  /**
   * Manual retry function
   */
  const retry = useCallback(async (apiCall, callOptions = {}) => {
    if (!error || !error.isRetryable) {
      return;
    }

    try {
      setIsRetrying(true);
      const result = await execute(apiCall, callOptions);
      setIsRetrying(false);
      return result;
    } catch (err) {
      setIsRetrying(false);
      throw err;
    }
  }, [error, execute]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
    setIsRetrying(false);
  }, []);

  /**
   * Get recovery options for current error
   */
  const getRecoveryOptions = useCallback(() => {
    if (!error) return [];
    return getRecoveryStrategies(error);
  }, [error]);

  /**
   * Execute recovery strategy
   */
  const executeRecovery = useCallback(async (strategy, apiCall, callOptions = {}) => {
    switch (strategy.type) {
      case ERROR_RECOVERY_STRATEGIES.RETRY:
        return await retry(apiCall, callOptions);
      
      case ERROR_RECOVERY_STRATEGIES.REFRESH:
        window.location.reload();
        break;
      
      case ERROR_RECOVERY_STRATEGIES.REDIRECT:
        window.location.href = strategy.url;
        break;
      
      case ERROR_RECOVERY_STRATEGIES.FALLBACK:
        // Fallback logic would be implemented by the calling component
        if (callOptions.onFallback) {
          callOptions.onFallback();
        }
        break;
      
      default:
        console.warn('Unknown recovery strategy:', strategy.type);
    }
  }, [retry]);

  return {
    loading,
    error,
    retryCount,
    isRetrying,
    execute,
    executeWithRetry,
    retry,
    clearError,
    getRecoveryOptions,
    executeRecovery,
    apiClient
  };
};

/**
 * Enhanced form submission hook with validation and error handling
 */
export const useEnhancedFormSubmit = (options = {}) => {
  const { 
    executeWithRetry, 
    loading, 
    error, 
    clearError, 
    getRecoveryOptions, 
    executeRecovery 
  } = useEnhancedApi(options);
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitForm = useCallback(async (apiCall, formData, callOptions = {}) => {
    // Clear previous errors
    setValidationErrors({});
    clearError();
    setIsSubmitting(true);

    try {
      const result = await executeWithRetry(apiCall, {
        operation: 'Form Submission',
        successMessage: 'Form submitted successfully',
        ...callOptions
      });
      
      return result;
    } catch (err) {
      // Handle validation errors
      if (err.category === 'validation' && err.technicalDetails) {
        setValidationErrors(err.technicalDetails);
      }
      
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [executeWithRetry, clearError]);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  return {
    submitForm,
    loading: loading || isSubmitting,
    error,
    validationErrors,
    clearError,
    clearValidationErrors,
    getRecoveryOptions,
    executeRecovery
  };
};

/**
 * Enhanced data fetching hook with caching and error handling
 */
export const useEnhancedDataFetch = (options = {}) => {
  const { 
    executeWithRetry, 
    loading, 
    error, 
    clearError, 
    getRecoveryOptions, 
    executeRecovery 
  } = useEnhancedApi(options);
  
  const [data, setData] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const cacheRef = useRef(new Map());

  const {
    cacheKey = null,
    cacheTimeout = 5 * 60 * 1000, // 5 minutes
    staleWhileRevalidate = false
  } = options;

  const fetchData = useCallback(async (apiCall, callOptions = {}) => {
    const {
      useCache = !!cacheKey,
      ...otherOptions
    } = callOptions;

    // Check cache first
    if (useCache && cacheKey) {
      const cached = cacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < cacheTimeout) {
        setData(cached.data);
        setLastFetch(cached.timestamp);
        
        if (!staleWhileRevalidate) {
          return cached.data;
        }
      }
    }

    try {
      const result = await executeWithRetry(apiCall, {
        operation: 'Data Fetch',
        ...otherOptions
      });
      
      setData(result);
      setLastFetch(Date.now());
      
      // Cache the result
      if (useCache && cacheKey) {
        cacheRef.current.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }
      
      return result;
    } catch (err) {
      // If we have stale cached data, return it as fallback
      if (staleWhileRevalidate && cacheKey) {
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
          setData(cached.data);
          return cached.data;
        }
      }
      
      throw err;
    }
  }, [executeWithRetry, cacheKey, cacheTimeout, staleWhileRevalidate]);

  const refetch = useCallback((apiCall, callOptions = {}) => {
    return fetchData(apiCall, { ...callOptions, useCache: false });
  }, [fetchData]);

  const clearData = useCallback(() => {
    setData(null);
    setLastFetch(null);
    if (cacheKey) {
      cacheRef.current.delete(cacheKey);
    }
  }, [cacheKey]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return {
    data,
    loading,
    error,
    lastFetch,
    fetchData,
    refetch,
    clearData,
    clearCache,
    clearError,
    getRecoveryOptions,
    executeRecovery
  };
};

/**
 * Enhanced file upload hook with progress tracking and error handling
 */
export const useEnhancedFileUpload = (options = {}) => {
  const { getToken } = useAuth();
  const { showError, showSuccess } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;

  const {
    component = 'FileUpload',
    showErrorToasts = true,
    showSuccessToasts = true
  } = options;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const uploadFile = useCallback(async (endpoint, file, additionalData = {}, callOptions = {}) => {
    const {
      onSuccess = null,
      onError = null,
      showErrorToast = showErrorToasts,
      showSuccessToast = showSuccessToasts
    } = callOptions;

    const errorContext = createErrorContext('File Upload', component, {
      endpoint,
      fileName: file?.name,
      fileSize: file?.size
    });

    try {
      setUploading(true);
      setProgress(0);
      setError(null);

      const result = await apiClient.uploadFile(
        endpoint,
        file,
        setProgress,
        additionalData
      );

      if (showSuccessToast) {
        const fileName = file ? file.name : (additionalData.original_filename || 'File');
        showSuccess(`File "${fileName}" uploaded successfully`);
      }

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      const standardizedError = standardizeError(err, errorContext);
      
      logError(standardizedError, errorContext);
      setError(standardizedError);

      if (showErrorToast) {
        showError(standardizedError.userMessage);
      }

      if (onError) {
        onError(standardizedError);
      }

      throw standardizedError;
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [apiClient, showError, showSuccess, component, showErrorToasts, showSuccessToasts]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadFile,
    uploading,
    progress,
    error,
    clearError
  };
};
